import {
  V1ValueScalarType,
  type V1CollectionField,
} from '@/generated/hooks/bishop/models';

import type { TRainValueJs } from '../utils/value';

// Reads the arguments of quill2's replace-the-whole-list tools and compares them
// against what exists now: `update_collection_schema` against a collection's
// fields, `set_discovered` against a connection's enabled entities. Both PUT the
// complete set, so what they leave out is what the user needs to see.
//
// Kept free of JSX and of copy — the card and its wording live with the
// renderer — so the classification can be tested on its own.

/**
 * The field-type names `create_collection` and `update_collection_schema`
 * accept, resolved to bishop's scalar types so a proposed field can be compared
 * with a stored one. Unknown names fall back to text, matching agent-quill's
 * own coercion.
 */
const SCALAR_TYPES: Readonly<Record<string, V1ValueScalarType>> = {
  text: V1ValueScalarType.VALUE_SCALAR_TYPE_TEXT,
  number: V1ValueScalarType.VALUE_SCALAR_TYPE_NUMBER,
  boolean: V1ValueScalarType.VALUE_SCALAR_TYPE_BOOLEAN,
  date: V1ValueScalarType.VALUE_SCALAR_TYPE_DATE,
  datetime: V1ValueScalarType.VALUE_SCALAR_TYPE_DATETIME,
  time: V1ValueScalarType.VALUE_SCALAR_TYPE_TIME,
  file: V1ValueScalarType.VALUE_SCALAR_TYPE_FILE,
  timedelta: V1ValueScalarType.VALUE_SCALAR_TYPE_TIMEDELTA,
};

const asScalarType = (value: TRainValueJs | undefined): V1ValueScalarType =>
  (typeof value === 'string' && SCALAR_TYPES[value.toLowerCase()]) ||
  V1ValueScalarType.VALUE_SCALAR_TYPE_TEXT;

/** A string argument worth showing, or nothing. Blank is nothing. */
export const asTrimmed = (
  value: TRainValueJs | undefined,
): string | undefined => {
  if (typeof value !== 'string') return undefined;
  return value.trim() || undefined;
};

/** One field, as the tool proposes it or as the collection already has it. */
export interface IQuillSchemaField {
  name: string;
  /** Undefined for a field bishop reports with a non-scalar type. */
  scalarType?: V1ValueScalarType;
  required: boolean;
  unique: boolean;
}

type TQuillFieldChangeKind = 'added' | 'changed' | 'removed' | 'unchanged';

/** What about a kept field is different. A field can differ in several ways. */
type TQuillFieldChangeReason = 'type' | 'required' | 'unique';

export interface IQuillSchemaFieldChange {
  /** The proposed field, or the one being dropped when `kind` is `removed`. */
  field: IQuillSchemaField;
  kind: TQuillFieldChangeKind;
  reasons: TQuillFieldChangeReason[];
  /** The type the field had, when its type is one of the reasons. */
  previousScalarType?: V1ValueScalarType;
}

/**
 * Reads a `fields` argument. Entries with no name are dropped — a malformed or
 * half-streamed field has nothing to show and must not become a blank row —
 * and a name is only taken once, so a repeated name cannot be diffed twice.
 */
export const parseProposedFields = (
  value: TRainValueJs | undefined,
): IQuillSchemaField[] => {
  if (!Array.isArray(value)) return [];
  const byName = new Map<string, IQuillSchemaField>();
  for (const entry of value) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
    const record = entry as { [key: string]: TRainValueJs };
    const name = asTrimmed(record.name);
    if (!name || byName.has(name)) continue;
    byName.set(name, {
      name,
      scalarType: asScalarType(record.type),
      required: record.required === true,
      unique: record.unique === true,
    });
  }
  return [...byName.values()];
};

/**
 * A stored type, or nothing when bishop has not told us one. Proto3 JSON usually
 * omits an enum default, but a gateway that emits defaults sends
 * `VALUE_SCALAR_TYPE_UNSPECIFIED` — which must read as "unknown" and not as a
 * type in its own right, or {@link diffSchemaFields} sees two known-but-
 * different scalars and reports a field nobody touched as changed.
 */
const asStoredScalarType = (
  scalarType: V1ValueScalarType | undefined,
): V1ValueScalarType | undefined =>
  scalarType === V1ValueScalarType.VALUE_SCALAR_TYPE_UNSPECIFIED
    ? undefined
    : scalarType;

/** The schema a collection has today, in the same shape as the proposal. */
export const readCurrentFields = (
  fields: V1CollectionField[] | undefined,
): IQuillSchemaField[] =>
  (fields ?? []).flatMap(field =>
    field.name
      ? [
          {
            name: field.name,
            scalarType: asStoredScalarType(field.type?.scalar_type),
            required: field.required === true,
            unique: field.unique === true,
          },
        ]
      : [],
  );

/** One name in a set, and what is happening to it. */
export interface IQuillNameChange {
  name: string;
  kind: 'added' | 'removed' | 'unchanged';
}

/**
 * Classifies a proposed set of names against the current one, the way
 * {@link diffSchemaFields} does for fields. `set_discovered` is a PUT: `what`
 * carries the complete set, so a name it leaves out is switched off. Removals
 * come first, then additions, and what is already on is left to a count.
 *
 * Only the enabled names are knowable — bdk reports `discovered` and nothing
 * else, so "off" means absent from it rather than listed anywhere.
 */
export const diffNames = (
  proposed: readonly string[],
  current: readonly string[],
): IQuillNameChange[] => {
  const proposedSet = new Set(proposed);
  const currentSet = new Set(current);

  return [
    ...current
      .filter(name => !proposedSet.has(name))
      .map<IQuillNameChange>(name => ({ name, kind: 'removed' })),
    ...proposed
      .filter(name => !currentSet.has(name))
      .map<IQuillNameChange>(name => ({ name, kind: 'added' })),
    ...proposed
      .filter(name => currentSet.has(name))
      .map<IQuillNameChange>(name => ({ name, kind: 'unchanged' })),
  ];
};

/**
 * Reads a `List<String>` argument, dropping anything that is not a name and
 * taking each name once, so a repeat cannot be diffed twice.
 */
export const parseNames = (value: TRainValueJs | undefined): string[] => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  for (const entry of value) {
    const name = asTrimmed(entry);
    if (name) seen.add(name);
  }
  return [...seen];
};

const compareField = (
  proposed: IQuillSchemaField,
  current: IQuillSchemaField,
): IQuillSchemaFieldChange => {
  const reasons: TQuillFieldChangeReason[] = [];
  // Only claim a type change when both types are known scalars: bishop reports
  // a wrapped type (a list, an optional) with no `scalar_type`, and the tool
  // can only ever send a scalar, so an unknown type is not evidence of a change.
  const typeChanged =
    current.scalarType !== undefined &&
    proposed.scalarType !== undefined &&
    current.scalarType !== proposed.scalarType;
  if (typeChanged) reasons.push('type');
  if (proposed.required !== current.required) reasons.push('required');
  if (proposed.unique !== current.unique) reasons.push('unique');

  return {
    field: proposed,
    kind: reasons.length > 0 ? 'changed' : 'unchanged',
    reasons,
    previousScalarType: typeChanged ? current.scalarType : undefined,
  };
};

/**
 * Classifies a proposed schema against the current one. The tool replaces the
 * field list wholesale, so a field the proposal leaves out is dropped — that
 * omission is the change the user most needs to see, and it is the only one the
 * arguments alone cannot show. Removals come first for that reason, then edits,
 * then additions.
 *
 * A field's description is not compared: it changes nothing about the data the
 * collection can hold, and flagging it would bury the changes that do.
 */
export const diffSchemaFields = (
  proposed: IQuillSchemaField[],
  current: IQuillSchemaField[],
): IQuillSchemaFieldChange[] => {
  const currentByName = new Map(current.map(field => [field.name, field]));
  const proposedNames = new Set(proposed.map(field => field.name));

  const removed = current
    .filter(field => !proposedNames.has(field.name))
    .map<IQuillSchemaFieldChange>(field => ({
      field,
      kind: 'removed',
      reasons: [],
    }));

  const kept: IQuillSchemaFieldChange[] = [];
  const added: IQuillSchemaFieldChange[] = [];
  for (const field of proposed) {
    const existing = currentByName.get(field.name);
    if (existing) kept.push(compareField(field, existing));
    else added.push({ field, kind: 'added', reasons: [] });
  }

  return [
    ...removed,
    ...kept.filter(change => change.kind === 'changed'),
    ...added,
    ...kept.filter(change => change.kind === 'unchanged'),
  ];
};
